/**
 * @module linkage/chartTypeSwitch/reconcileForChartType/reconcileDualAxis
 * @description 切图 L2 — 组合图 / 折线双轴结构互转（metric ↔ metrics、chartType 归一）
 */

import { type TChartType } from '@dbl/core/chart';
import {
  convertDualAxisForChartType,
  isDualAxisChartTypeConvertTarget,
  layerHasDualAxisObjects,
} from '../../../settingGroups/common/DualAxis/dualAxisChartTypeConvert';
import type { QBStateSnapshot } from '../../../types';
import { applyLayerPatch } from './applyLayerPatch';
import type { IReconcileLayerResult } from './reconcileTypes';

const DUAL_AXIS_PATCH_KEYS = [
  'primaryYAxis',
  'secondYAxis',
  'dualYAxisEnabled',
] as const;

function buildDualAxisPatches(
  layer: Record<string, unknown>,
  nextLayer: Record<string, unknown>,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const key of DUAL_AXIS_PATCH_KEYS) {
    if (nextLayer[key] !== layer[key]) {
      patch[key] = nextLayer[key];
    }
  }
  return patch;
}

function reconcileDualAxisLayer(
  layer: Record<string, unknown>,
  chartType: TChartType,
  columnMetrics: unknown[],
): { next: Record<string, unknown>; patch: Record<string, unknown> } {
  if (!layerHasDualAxisObjects(layer)) {
    return { next: layer, patch: {} };
  }

  const converted = convertDualAxisForChartType(
    chartType,
    layer.primaryYAxis,
    layer.secondYAxis,
    columnMetrics,
  );

  const axisPatch: Record<string, unknown> = {
    primaryYAxis: converted.primaryYAxis,
    secondYAxis: converted.secondYAxis,
  };
  if (converted.dualYAxisEnabled !== undefined) {
    axisPatch.dualYAxisEnabled = converted.dualYAxisEnabled;
  }

  const next = applyLayerPatch(layer, axisPatch);
  const patch = buildDualAxisPatches(layer, next);

  return { next, patch };
}

/**
 * 切到折线 / 组合图时，将 primaryYAxis / secondYAxis 转为目标图类型协议。
 * 无主次轴对象或非目标图类型时不处理。
 */
export function reconcileDualAxisForChartType(
  chartType: TChartType,
  qb: QBStateSnapshot,
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
): IReconcileLayerResult {
  if (!isDualAxisChartTypeConvertTarget(chartType)) {
    return { base, draft, basePatch: {}, draftPatch: {} };
  }

  const baseResult = reconcileDualAxisLayer(
    base,
    chartType,
    qb.columnMetrics,
  );
  const draftResult = reconcileDualAxisLayer(
    draft,
    chartType,
    qb.columnMetrics,
  );

  return {
    base: baseResult.next,
    draft: draftResult.next,
    basePatch: baseResult.patch,
    draftPatch: draftResult.patch,
  };
}

